import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../../../../context/LoadingContext";
import {
  fetchData,
  postQuery,
} from "../../../Requirement/services/RequirementServices";
import AlertModal from "../../../Requirement/components/common/AlerModel";
import { jwtDecode } from "jwt-decode";
import {
  FormControl,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const SecurityInformation = () => {
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const [activeUser, setActiveUser] = useState("");
  const [buyerInfo, setBuyerInfo] = useState([]);
  const [userIdentities, setUserIdentities] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const { loading, setLoading } = useLoading();

  // Check if form should be disabled
  const isFormDisabled = userIdentities !== undefined && userIdentities !== "";

  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
  };

  // Toggle password visibility
  const handleClickShowCurrentPassword = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };
  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  
  // Password validation function returning validation states
  const validatePassword = (password) => {
    const upperCaseRegex = /[A-Z]/;
    const lowerCaseRegex = /[a-z]/;
    const numberRegex = /[0-9]/;
    const symbolRegex = /[@#$%*?-]/;
    const minLength = 8;

    return {
      hasUpperCase: upperCaseRegex.test(password),
      hasLowerCase: lowerCaseRegex.test(password),
      hasNumber: numberRegex.test(password),
      hasSymbol: symbolRegex.test(password),
      hasMinLength: password.length >= minLength,
      matches: password === confirmPassword && password !== "",
    };
  };

  const validation = validatePassword(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if form is disabled
    if (isFormDisabled) {
      return;
    }

    // Validate inputs
    const errors = [];
    // Check if all validations pass
    if (
      !validation.hasUpperCase ||
      !validation.hasLowerCase ||
      !validation.hasNumber ||
      !validation.hasSymbol ||
      !validation.hasMinLength ||
      !validation.matches
    ) {
      // return;
      //   errors.push("New password format not correct.");
    }

    // Validate current password
    if (!currentPassword.trim()) {
      errors.push("Please enter the current password.");
    }

    // Validate password match
    if (newPassword !== confirmPassword) {
      errors.push("New & Confirm Passwords do not match.");
    }

    // Validate password requirements
    const passwordErrors = validatePassword(newPassword);
    if (Object.values(passwordErrors).some(v => !v)) {
      errors.push("New password format not correct.");
    }

    // If there are errors, show them in the modal
    if (errors.length > 0) {
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: errors.join(" "),
        autoClose: false,
      });
      return;
    }
    setLoading(true);
    try {
      // Submit the reset code and new password to AWS Amplify
      // Get the currently authenticated user
      const user = await Auth.currentAuthenticatedUser();

      // Change password by verifying old password and setting new one
      const res = await Auth.changePassword(user, currentPassword, newPassword);
      if (res) {
        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: "Password change successful! Redirecting to login...",
          autoClose: false,
        });
        setTimeout(() => {
          navigate(`/`);
        }, 2000);
      }
    } catch (error) {
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || "An error occurred. Please try again.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const getBuyerInfo = useCallback(async (buyerEmail) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { EmailID: buyerEmail },
      });
      console.log("listAMTPLUsers::", response.items);
      setBuyerInfo(response.items || []);
    } catch (error) {
      console.error("Error loading buyer info:", error);
      setBuyerInfo([]);
    }
  }, []);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_GLOBAL_PARTNERS")) {
          await Auth.signOut();
          navigate("/login/global-partner");
        } else {
          console.log('cog user.attributes identities@@',user.attributes.identities);
          setActiveUser(user.attributes.email);
          setUserIdentities(user.attributes.identities);
          getBuyerInfo(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/global-partner");
      }
    };
    checkUser();
  }, [navigate, getBuyerInfo]);

  return (
    <div className="container mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card p-4">
        <div className="table-header">
          <div className="header-text p-2">
            <div className="head-text">Security Settings</div>
            <div className="subtext">
              When you change your password, we keep you logged in to this
              device but may log you out from your other devices.
            </div>
          </div>
          <div className="head-btn">
            <div className="submit-btn-form">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                form="questionForm"
                disabled={isFormDisabled || submitted || loading}
                onClick={handleSubmit}
              >
                {isFormDisabled 
                  ? "Social Login" 
                  : submitted || loading 
                  ? "Submitting..." 
                  : "Submit"
                }
              </Button>
            </div>
          </div>
        </div>
        <form id="questionForm" className="row g-2">
          <div className="row g-2">
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Current Password <span className="mandatory">*</span>
                </Typography>

                <TextField
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  size="small"
                  disabled={isFormDisabled}
                  sx={{ 
                    backgroundColor: isFormDisabled ? "#f5f5f5" : "white" 
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowCurrentPassword}
                          edge="end"
                          disabled={isFormDisabled}
                        >
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </div>
          </div>
          <div className="row g-2">
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  New Password <span className="mandatory">*</span>
                </Typography>
                <TextField
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  size="small"
                  disabled={isFormDisabled}
                  sx={{ 
                    backgroundColor: isFormDisabled ? "#f5f5f5" : "white" 
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowNewPassword}
                          edge="end"
                          disabled={isFormDisabled}
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </div>
          </div>
          <div className="row g-2">
            <div className="col-md-4">
              <FormControl fullWidth>
                <Typography variant="body2">
                  Confirm Password <span className="mandatory">*</span>
                </Typography>
                <TextField
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  size="small"
                  disabled={isFormDisabled}
                  sx={{ 
                    backgroundColor: isFormDisabled ? "#f5f5f5" : "white" 
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                          disabled={isFormDisabled}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SecurityInformation;